/**
 * Copyright: ThoughtSpot Inc. 2020
 * Author: Thawsitt Naing (thawsitt.naing@thoughtspot.com)
 *
 * @fileoverview Muted Alert for Radiant
 */
import cx from 'classnames';
import React from 'react';
import { Button, ButtonSize } from '../../button';
import { Colors, Typography, Variants } from '../../typography';
import {
  ErrorCode,
  GLOBAL_MUTED_ALERT_DEFAULT_ILLUSTRATION,
  LOCAL_MUTED_ALERT_DEFAULT_ILLUSTRATION,
} from '../alert-constants';
import { AlertActionButton, AlertLink } from '../alert-interface';
import { createActionLink, getMutedAlertSVGPath } from '../alert-utils';
import styles from './muted-alert.module.scss';

export interface MutedAlertProps {
  /**
   * Whether the muted alert is a global alert.
   *
   * You don't need to set this manually.
   * It is only used by global alert context provider.
   *
   * @default false
   */
  global?: boolean;
  /**
   * Title of the alert message to show to the user
   *
   * @default undefined
   */
  title?: string;
  /**
   * Sub title of the global muted alert message to show to the user
   *
   * @default undefined
   */
  subtitle?: string;
  /**
   * Description of the alert message to show to the user
   *
   * @default undefined
   */
  description?: string;
  /**
   * Whether the alert is visible
   *
   * @default true
   */
  isVisible?: boolean;
  /**
   * Size of muted alert. Use 'small' for smaller vizs such as
   * headlines.
   *
   * In global muted alert only regular size works.
   *
   * @default 'regular'
   */
  size?: 'regular' | 'small';
  /**
   * Show a primary button with callback
   *
   * @default undefined
   */
  primaryAction?: AlertActionButton;
  /**
   * Show a secondary button with callback
   *
   * @default undefined
   */
  secondaryAction?: AlertActionButton;
  /**
   * Show a link for the user to click
   *
   * @default undefined
   */
  link?: AlertLink;
  /**
   * Error code associated with muted alert.
   * Will show a different svg based on error code.
   * If errorCode is not passed in, default
   * exclamation mark svg illustration will be shown.
   *
   * @default ''
   */
  errorCode?: ErrorCode;
  /**
   * Any additional class name for alert container
   *
   * @default ''
   */
  className?: string;
  /**
   * To show title of two different color
   * Title span for {Colors.gray} color text
   *
   * Apply only for global muted alert
   *
   * @default ''
   */
  coloredTitle?: string;
  /**
   * whether to show image
   *
   * @default false
   */
  imageVisible?: boolean;
  /**
   * tooltip for description
   *
   * @default undefined
   */
  descriptionTooltip?: string;

  /**
   * Height for description container
   *
   * @default undefined i.e. no limit
   */
  maxLinesForDescription?: number;
}

export const MutedAlert: React.FC<MutedAlertProps> = ({
  global = false,
  title,
  coloredTitle = '',
  subtitle,
  description,
  isVisible = true,
  size = 'regular',
  primaryAction,
  secondaryAction,
  link,
  errorCode,
  className = '',
  imageVisible = true,
  descriptionTooltip,
  maxLinesForDescription,
  ...restProps
}: MutedAlertProps) => {
  const localOrGlobal = global === true ? 'global' : 'local';

  const actionLink = link
    ? createActionLink(link, cx(styles.link, styles[size], styles[localOrGlobal]))
    : null;

  const textVariant = {
    global: {
      title: 'page-title',
      description: 'body-large',
      color: Colors.base,
    },
    local: {
      title: 'content-label-subhead',
      description: 'body-normal',
      color: Colors.grayLight,
    },
  };

  const buttonSizes = {
    small: 's',
    regular: 'm',
  };

  const buttonSize = buttonSizes[size] as ButtonSize;

  const primaryActionButton = primaryAction ? (
    <Button
      text={primaryAction.label}
      type={primaryAction?.type || 'primary'}
      size={buttonSize}
      backgroundType="light"
      className={cx(styles.primaryAction, primaryAction.className)}
      onClick={primaryAction.onClick}
      isDisabled={primaryAction.isDisabled}
      tooltip={primaryAction.tooltip}
    />
  ) : null;

  const secondaryActionButton = secondaryAction ? (
    <Button
      text={secondaryAction.label}
      type="secondary"
      size={buttonSize}
      backgroundType="light"
      className={cx(styles.secondaryAction, secondaryAction.className)}
      onClick={secondaryAction.onClick}
      isDisabled={secondaryAction.isDisabled}
      tooltip={secondaryAction.tooltip}
    />
  ) : null;

  const svgName =
    errorCode ||
    (global ? GLOBAL_MUTED_ALERT_DEFAULT_ILLUSTRATION : LOCAL_MUTED_ALERT_DEFAULT_ILLUSTRATION);

  const getIllustrationImage = () => {
    if (!imageVisible) {
      return undefined;
    }
    return (
      <img
        className={cx(styles.illustration, styles[size], styles[localOrGlobal])}
        src={getMutedAlertSVGPath(svgName)}
        alt=""
      />
    );
  };

  const containerClass = cx(className, styles.container, styles[size], styles[localOrGlobal]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={containerClass} {...restProps}>
      {getIllustrationImage()}
      <div className={cx(styles.content, styles[size], styles[localOrGlobal])}>
        {title && (
          <div className={cx(styles.titleWrapper, styles[localOrGlobal])}>
            <Typography
              variant={textVariant[localOrGlobal].title as Variants}
              color={textVariant[localOrGlobal].color}
              className={cx(styles.title, styles[localOrGlobal])}
            >
              {title}
            </Typography>
            {localOrGlobal === 'global' && coloredTitle && (
              <Typography
                variant={textVariant[localOrGlobal].title as Variants}
                color={Colors.gray}
                className={cx(styles.title, styles[localOrGlobal], styles.coloredTitle)}
              >
                {coloredTitle}
              </Typography>
            )}
          </div>
        )}
        {global && subtitle && (
          <Typography variant="content-label" color={Colors.base} className={styles.subTitle}>
            {subtitle}
          </Typography>
        )}
        {description && (
          <Typography
            variant={textVariant[localOrGlobal].description as Variants}
            color={textVariant[localOrGlobal].color}
            className={styles.description}
            tooltipConfig={{
              dataTooltipContent: descriptionTooltip as string,
              disableTooltip: !maxLinesForDescription,
            }}
            ellipsis={{ rows: maxLinesForDescription as number }}
          >
            {description}
          </Typography>
        )}

        <div className={cx(styles.actionButtons, styles[buttonSize])}>
          {secondaryActionButton}
          {primaryActionButton}
        </div>
        {actionLink}
      </div>
    </div>
  );
};
